import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import useGoBack from "@/hooks/useGoBack";
import { toast } from "@/lib/toast";
import { ArrowLeft, Trash2, Search, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import SearchClearButton from "@/components/ui/SearchClearButton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HeaderProfileGroup from "@/components/TopBar";
import DocumentStatusBadge from "@/components/documents/DocumentStatusBadge";
import TrashPurgeBanner from "@/components/shared/TrashPurgeBanner";

/**
 * DocumentsTrash — Corbeille du Module Documents (spec 2026-02-17 · point 5).
 *
 * Réutilise strictement le pattern UI de `TasksTrash.jsx` :
 *   • même en-tête (retour + icône rouge + titre + compteur)
 *   • même recherche plein texte
 *   • même tableau (Réf. · Objet · Dernier statut · Supprimé le · Par · Motif)
 *   • lecture seule ; la purge automatique est exécutée côté serveur
 *     30 jours après la suppression (cf. `_document_trash_purge_pass`).
 */
export default function DocumentsTrash() {
  const goBack = useGoBack("/documents");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/documents/trash/list");
        setItems(data || []);
      } catch (err) {
        toast.error("Impossible de charger la corbeille", { description: err.response?.data?.detail || "" });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const restoreDocument = async (docId) => {
    if (!isAdmin) return;
    try {
      await api.post(`/documents/${docId}/restore`);
      setItems((prev) => prev.filter((d) => d.id !== docId));
      toast.success("Document restauré", { description: "Les rappels et relances futurs sont réactivés" });
    } catch (err) {
      toast.error("Impossible", { description: err.response?.data?.detail || "" });
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((d) =>
      `${d.reference || ""} ${d.subject || d.title || ""} ${d.deleted_reason || ""} ${d.deleted_by_name || ""}`
        .toLowerCase().includes(term)
    );
  }, [items, q]);

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div className="flex flex-col gap-4 min-h-0" style={{ height: "calc(100vh - 80px)" }} data-testid="documents-trash-page">
      {/* Header */}
      <div className="flex items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack} className="gap-1" data-testid="doc-trash-back">
            <ArrowLeft size={14} /> Documents
          </Button>
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #EF4444 0%, #7F1D1D 100%)" }}
          >
            <Trash2 size={20} color="#fff" />
          </div>
          <div>
            <h1 className="text-3xl font-bold leading-none">Corbeille</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {items.length} document{items.length > 1 ? "s" : ""} supprimé{items.length > 1 ? "s" : ""} · purge automatique à 30 jours
            </p>
          </div>
        </div>
        <HeaderProfileGroup />
      </div>

      {/* Search */}
      <div className="relative w-full max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher réf, objet, motif ou auteur…"
          className="pl-9 pr-9"
          data-testid="doc-trash-search"
        />
        <SearchClearButton value={q} onClear={() => setQ("")} testid="doc-trash-search-clear" />
      </div>

      {/* Bannière de préavis purge (≤ 3 jours) */}
      <TrashPurgeBanner items={items} noun="document" />


      {/* Table */}
      <Card className="bg-card border-vertec-borderDark flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="flex-1 min-h-0" style={{ overflowY: "auto", overflowX: "hidden" }}>
          {loading ? (
            <div className="py-10 text-center text-muted-foreground italic">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground italic">
              {items.length === 0 ? "Aucun document dans la corbeille." : "Aucun résultat pour cette recherche."}
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="w-[130px]">Réf.</TableHead>
                  <TableHead>Objet</TableHead>
                  <TableHead>Dernier statut</TableHead>
                  <TableHead>Supprimé le</TableHead>
                  <TableHead>Par</TableHead>
                  <TableHead>Motif</TableHead>
                  {isAdmin && <TableHead className="w-[110px] text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((d) => (
                  <TableRow key={d.id} className="hover:bg-white/[0.02]" data-testid={`doc-trash-row-${d.id}`}>
                    <TableCell>
                      <span className="text-[11px] font-mono font-bold text-vertec-yellow tabular-nums">
                        {d.reference || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm font-medium">{d.subject || d.title || "—"}</TableCell>
                    <TableCell><DocumentStatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmt(d.deleted_at)}</TableCell>
                    <TableCell className="text-sm">{d.deleted_by_name || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[280px] truncate" title={d.deleted_reason || ""}>
                      {d.deleted_reason || <em>—</em>}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => restoreDocument(d.id)}
                          className="gap-1 border-status-success/40 text-status-success hover:bg-status-success/10 h-7 px-2 text-[11px]"
                          data-testid={`doc-trash-restore-${d.id}`}
                          title="Restaurer et réactiver les rappels futurs"
                        >
                          <RotateCcw size={11} /> Restaurer
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
}
